/* eslint-disable react/prop-types */ // TODO: add props
import { Component } from 'react';
import HtmlClassName from 'edrolo/domains/app/side-effects/HtmlClassName';
import { SortColumns } from './constants';
import LessonProgressHeaderRow from './LessonProgressHeaderRow';
import StudentLessonProgress from '../StudentLessonProgress';
import AppContainer from 'edrolo/domains/app/components/AppContainer';
import LoadingIndicator from 'edrolo/domains/app/components/LoadingIndicator';
import urlResolver from 'edrolo/utils/URLResolver';
import { AnonymizerToggle } from 'edrolo/domains/anonymizer';
import { Table } from 'edrolo/domains/teacherResultsTable';
import { constants as analyticsConstants } from 'edrolo/domains/studentProgressGrid/analytics';
import LessonProgressSuperHeaderRow from './LessonProgressSuperHeaderRow';
import { SCHOOL_CLASS_TYPE } from 'edrolo/domains/bySchoolClass/constants';
import { TrialFlag } from 'core/components/TrialFlag';
import styles from './ClassStudentLessonProgress.scss';

class ClassStudentLessonProgress extends Component {
  state = {
    sortBy: SortColumns.NAME,
    sortDescending: false,
  };

  // handle the default order and switching
  setSortColumn = (column) => {
    // first check if same column clicked: we're flipping the sort direction
    if (column === this.state.sortBy) {
      return this.setState({ sortDescending: !this.state.sortDescending });
    }

    // if we're sorting by name then default to ascending
    if (column === SortColumns.NAME) {
      return this.setState({
        sortBy: column,
        sortDescending: false,
      });
    }

    // otherwise sort by the clicked column descending
    return this.setState({
      sortBy: column,
      sortDescending: true,
    });
  };

  buildChunkUrl = (chunkIndex) =>
    urlResolver.reverse('chunkview-chunk', {
      classId: this.props.classId,
      lessonId: this.props.lesson && this.props.lesson.get('id'),
      index: chunkIndex,
    });

  buildStudentCourseProgressUrl = (profileId) =>
    urlResolver.reverse('student-course-progress', {
      classId: this.props.classId,
      profileId,
    });

  buildStudentNotebookUrl = (profileId) =>
    urlResolver.reverse('student-notes-notebook', {
      classId: this.props.classId,
      lessonId: this.props.lesson && this.props.lesson.get('id'),
      profileId,
    });

  // sort by takes a value mapper and an optional comparator.
  // comparator needed to compare strings
  // here we just map each search to it's required value
  sort(classLessonData) {
    let valueMapper = (f) => f;
    let comparator;

    switch (this.state.sortBy) {
      case SortColumns.NAME:
        valueMapper = (row) =>
          `${row.getIn(['profile', 'last_name'])}, ${row.getIn([
            'profile',
            'name',
          ])}`;
        comparator = (a, b) => a.localeCompare(b);
        break;

      case SortColumns.VIEWED:
        valueMapper = (row) => row.getIn(['progress', 'chunk_visit_count']);
        break;

      case SortColumns.QUIZZES:
        valueMapper = (row) => row.getIn(['summary', 'quizResultsTally']);
        break;

      case SortColumns.SELF_RATING:
        valueMapper = (row) => row.getIn(['summary', 'selfRating']);
        break;

      case SortColumns.STUDENT_NOTES:
        valueMapper = (row) => row.getIn(['summary', 'noteTaken']);
        break;

      default:
        break;
    }

    const sorted = classLessonData.sortBy(valueMapper, comparator);

    // if it's desc then reverse the order
    return this.state.sortDescending ? sorted.reverse() : sorted;
  }

  render() {
    const {
      lesson,
      chunks,
      classLessonData,
      classDetail,
      totalQuizMarks,
      classLessonProgress,
      showStudentNotes,
    } = this.props;

    let content;
    if (!lesson || !chunks || !classLessonData || !classDetail) {
      content = <LoadingIndicator loadingText="Loading Student Progress" />;
    } else {
      const navigatedFrom = {
        url: this.props.location.pathname,
        label: `Class progress - ${this.props.lesson.get('title')}`,
      };

      const gridData = this.sort(classLessonData);
      const studentProgressSuccessMeasureResponses = classLessonProgress?.get(
        'student_progress_success_measure_responses'
      );

      // lesson has no chunks with success measure
      const shouldShowSelfRating =
        chunks.filter((chunk) => chunk.get('success_measure')).size === 0;

      const showGuidingQuestionSuperHeader = chunks.some((chunk) =>
        chunk.get('success_measure')
      );

      const profileRows = gridData.map((row) => {
        return (
          <StudentLessonProgress
            key={row.getIn(['profile', 'id'])}
            buildChunkUrl={this.buildChunkUrl}
            buildStudentCourseProgressUrl={this.buildStudentCourseProgressUrl}
            buildStudentNotebookUrl={this.buildStudentNotebookUrl}
            progress={row}
            chunks={chunks}
            totalQuizMarks={totalQuizMarks}
            navigatedFrom={navigatedFrom}
            analyticsPayload={{
              origin: analyticsConstants.origin.CLASS_STUDENT_LESSON_PROGRESS,
              classId: classDetail.get('id'),
              lessonId: lesson && lesson.get('id'),
              studentUserId: row.getIn(['profile', 'user']),
            }}
            studentProgressSuccessMeasureResponses={
              studentProgressSuccessMeasureResponses
            }
            shouldShowSelfRating={shouldShowSelfRating}
            shouldShowStudentNotes={showStudentNotes}
          />
        );
      });

      const classType = classDetail?.get('class_type');

      const isTrial =
        classType === SCHOOL_CLASS_TYPE.student_trial ||
        classType === SCHOOL_CLASS_TYPE.teacher_trial;

      content = (
        <div data-test="class-student-lesson-progress">
          <HtmlClassName className="horizontal-scroll" />
          <AnonymizerToggle />
          <h1 className={styles.courseTitle} data-test={'course-title'}>
            {this.props.lesson.get('title')}
          </h1>

          <div className={styles.classTitle}>
            <h2 className={styles.h2}>{this.props.classDetail.get('title')}</h2>

            {isTrial && (
              <TrialFlag
                expirationDate={this.props.classDetail.get('expiration_date')}
                large
              />
            )}
          </div>

          <Table>
            <Table.Head>
              {showGuidingQuestionSuperHeader && (
                <LessonProgressSuperHeaderRow
                  chunks={chunks}
                  shouldShowStudentNotes={showStudentNotes}
                />
              )}
              <LessonProgressHeaderRow
                chunks={chunks}
                onSort={this.setSortColumn}
                lessonProgressData={classLessonData}
                currentSortColumn={this.state.sortBy}
                isDescending={this.state.sortDescending}
                studentProgressSuccessMeasureResponses={
                  studentProgressSuccessMeasureResponses
                }
                shouldShowSelfRating={shouldShowSelfRating}
                shouldShowStudentNotes={showStudentNotes}
              />
            </Table.Head>
            <tbody>{profileRows}</tbody>
          </Table>
        </div>
      );
    }

    return (
      <AppContainer breadcrumbs={this.props.breadcrumbs}>
        {content}
      </AppContainer>
    );
  }
}

export default ClassStudentLessonProgress;
